import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { name } from '../symbols'
import { EditorFeatureConfigSymbol, IPropsStore, PageFeatureConfigSymbol, Props } from '@wix/thunderbolt-symbols'
import type {
	WixCustomElementComponentEditorConfig,
	WixCustomElementComponentEditorFeatureAPI,
	WixCustomElementComponentEditorFeature,
	CustomElementComponent,
} from '../types'
import { generateWixCodeUrl } from '../utils'
import { WixCustomElementComponentPageConfig } from '../types'

const corvidContentStates: Record<string, string> = {}

/**
 * This config returns public method which will be used by platform itself
 * for the flow, when content of the file in corvid were changed.
 *
 * In a case of such changes, file will have a new content, but URL to this
 * file will stay the same. Because of this iframe inside the
 * PreviewCustomElementComponent will not refetch data.
 *
 * So to update CustomElementComponent, TB will call "onCorvidContentUpdate" with URL
 * to updated corvid file, and a new updated cacheKiller query param.
 */
const dsCustomElementComponentAPI = (
	editorConfig: WixCustomElementComponentEditorConfig,
	propsStore: IPropsStore
): WixCustomElementComponentEditorFeatureAPI => {
	const updateIFrames = (updatedContentURL: string, cacheKillerCounter: number) => {
		const componentsIds = Object.keys(corvidContentStates).filter(
			(componentId) => corvidContentStates[componentId] === updatedContentURL
		)

		componentsIds.forEach(async (id) => {
			const { wixCodeBundlersUrlData } = editorConfig
			const previewUrl = generateWixCodeUrl({
				wixCodeBundlersUrlData,
				url: updatedContentURL,
				cacheKillerCounter,
			})
			propsStore.update({ [id]: { previewUrl } })
		})
	}

	return {
		onCorvidContentUpdate(url, cacheKillerCounter) {
			updateIFrames(url, cacheKillerCounter)
		},
	}
}

type UpdateProps = {
	isPremiumUser: boolean
	hasDomainFeature: boolean
	isAdsFree: boolean
	previewUrl?: string
	isAllowedForFreeSite: boolean
}

const dsCustomElementComponent = (
	editorConfig: WixCustomElementComponentEditorConfig,
	propsStore: IPropsStore,
	pageFeatureConfig?: WixCustomElementComponentPageConfig
): WixCustomElementComponentEditorFeature => {
	const getPreviewUrl = (component: CustomElementComponent) => {
		const { wixCodeBundlersUrlData } = editorConfig
		const { compId, url, hostedInCorvid, appDefId } = component

		let previewUrl = url

		if (hostedInCorvid) {
			corvidContentStates[compId] = url
			previewUrl = generateWixCodeUrl({ wixCodeBundlersUrlData, url, appDefId })
		} else if (corvidContentStates[compId] !== undefined) {
			delete corvidContentStates[compId]
		}

		return previewUrl
	}

	return {
		componentTypes: ['CustomElementComponent'],
		componentWillMount(component) {
			const { isPremiumUser, hasDomainFeature, isAdsFree, widgetsToRenderOnFreeSites } = editorConfig
			const { id } = component
			const currentComp = pageFeatureConfig?.customElements.find((comp) => comp.compId === id)

			const isAllowedForFreeSite = !!(currentComp?.widgetId && widgetsToRenderOnFreeSites[currentComp?.widgetId])
			const props: UpdateProps = { isPremiumUser, hasDomainFeature, isAdsFree, isAllowedForFreeSite }
			if (currentComp) {
				props.previewUrl = getPreviewUrl(currentComp)
			}
			component.updateProps(props)

			// We should remove component from map on unmount
			return () => {
				if (corvidContentStates[id] !== undefined) {
					delete corvidContentStates[id]
				}
			}
		},
		featureName: name,
		handlers: {
			onComponentUpdate: (component) => {
				const previewUrl = getPreviewUrl(component)
				propsStore.update({ [component.compId]: { previewUrl } })
			},
		},
	}
}

export const DsCustomElementComponentAPIFactory = withDependencies(
	[named(EditorFeatureConfigSymbol, name), Props],
	dsCustomElementComponentAPI
)

export const DsCustomElementComponentFactory = withDependencies(
	[named(EditorFeatureConfigSymbol, name), Props, named(PageFeatureConfigSymbol, name)],
	dsCustomElementComponent
)
